import { useCallback, useEffect, useState } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import isEqual from 'lodash/isEqual'

import {
  clearAppliance,
  getAppliance,
  recreateTunnels,
  registerApplianceObserver,
  removeAppliance,
  restartAppliance,
  unregisterApplianceObserver,
  updateAppliance,
} from '../../../redux/actions/applianceActions'
import {
  Appliance,
  ApplianceCommandInfo,
  ApplianceProxySettings,
  ApplianceSettings,
  ApplianceType,
  Group,
  InputAdminStatus,
  MatroxE4ApplianceSettings,
  OutputAdminStatus,
  Role,
  UpdateAppliancePayload,
} from 'common/api/v1/types'
import { Api, AppDispatch, GlobalState, useRoutes } from '../../../store'
import {
  alarmsThatDisablePort,
  formTransform,
  getApplianceOwnerId,
  useIntervalState,
  useSettingsSelector,
  useUser,
} from '../../../utils'
import Pendable from '../../common/Pendable'
import Wrapper from '../../common/Wrapper'
import { ConfirmationDialogResult, useConfirmationDialog } from '../../common/ConfirmDialog'
import Interfaces, { ISharedPort } from './ApplianceForm/Interfaces'
import Settings from './ApplianceForm/Settings/Settings'
import { AdvancedSettings } from './ApplianceForm/Settings/AdvancedSettings'
import { ButtonsPane, Form, RHF, SafeRouting, Paper } from '../../common/Form'
import Meta from './ApplianceForm/Meta'
import { RemoveDialog } from './RemoveDialog'
import InputsOutputs from './ApplianceForm/InputsOutputs'
import ApplicationError from '../../common/ApplicationError'
import { AffectedOutput, ChangeRegionDialog } from './ChangeRegionDialog'
import { enqueueErrorSnackbar } from '../../../redux/actions/notificationActions'
import { ChangeSecondaryRegionDialog } from './ChangeSecondaryRegionDialog'
import { RemoveSecondaryRegionDialog } from './RemoveSecondaryRegionDialog'
import Tunnels from './ApplianceForm/Tunnels'
import { Pcaps } from './ApplianceForm/Pcaps'
import { supportsPcap } from 'common/versions'
import { TagInputField } from '../../common/TagInputField'
import { pick } from 'common/utils'
import {
  CommandList,
  CommandStatus,
  commandFailed,
  commandInProgress,
  commandNotStarted,
  commandSuccess,
} from './CommandList'

export type ApplianceEditState = UpdateAppliancePayload & { id: string }

export const Edit = () => {
  const { id: applianceId } = useParams() as { id: string }
  const routes = useRoutes()
  const navigate = useNavigate()
  const user = useUser()
  const dispatch = useDispatch<AppDispatch>()
  const appliance = useSelector(({ appliancesReducer }: GlobalState) => appliancesReducer.appliance, shallowEqual)
  const buildInfo = useSelector(
    ({ buildInfoReducer }: GlobalState) =>
      buildInfoReducer.buildInfo ? pick(buildInfoReducer.buildInfo, 'release') : undefined,
    shallowEqual,
  )
  const releaseVersion = buildInfo?.release || 'dummy'
  const { saving, restarting, loading, error, recreatingTunnels } = useSelector(
    ({ appliancesReducer }: GlobalState) => ({
      saving: appliancesReducer.saving,
      restarting: appliancesReducer.restarting,
      loading: appliancesReducer.loading,
      error: appliancesReducer.error,
      recreatingTunnels: appliancesReducer.recreatingTunnels,
    }),
    shallowEqual,
  )
  const { showConfirmation, showConfirmationAsync, hideConfirmation } = useConfirmationDialog()
  const { devMode } = useSettingsSelector()

  const [isFetchingOutputsAffectedByRegionChange, setIsFetchingOutputsAffectedByRegionChange] = useState(false)

  const commandStatuses = useCommandStatuses(applianceId)
  const hasCommands = commandStatuses.hasResult && commandStatuses.result.length > 0

  useEffect(() => {
    dispatch(registerApplianceObserver({ applianceIds: [applianceId] }))
    applianceId && dispatch(getAppliance(applianceId))
    return () => {
      dispatch(unregisterApplianceObserver({ applianceIds: [applianceId] }))
      dispatch(clearAppliance())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchOutputsAffectedByRegionChange = useCallback(async () => {
    // Set high rowsPerPage because we want all affected outputs
    const outputs = await Api.appliancesApi.listApplianceOutputs(applianceId, {
      pageNumber: '0',
      rowsPerPage: '1000000',
    })

    // If output or its source input is disabled the output isn't considered affected
    const affectedOutputs: AffectedOutput[] = outputs.items
      .filter((x) => x.outputAdminStatus === OutputAdminStatus.on && x.inputAdminStatus === InputAdminStatus.on)
      .map((x) => {
        return { id: x.outputId, name: x.outputName }
      })

    return affectedOutputs
  }, [applianceId])

  const onSubmit = async (
    appliance: Appliance,
    transformedValues: Pick<UpdateAppliancePayload, 'region' | 'secondaryRegion' | 'acceptDisruption' | 'settings'> & {
      ports: Array<ISharedPort>
    },
  ) => {
    const values = {
      ...transformedValues,
      ports: transformedValues.ports.map((port) => ({ ...port, networks: port._networks?.map((n) => n.id) })),
    }

    const region = values.region
    if (region === undefined) {
      // Region is required
      return
    }

    const hasRegionChanged = initialValues!.region?.id !== region.id

    const initSecondaryRegion = initialValues!.secondaryRegion
    const secondareRegionWasInitiallySet = initSecondaryRegion !== undefined && initSecondaryRegion !== null
    const hasSecondaryRegionChanged =
      secondareRegionWasInitiallySet && initSecondaryRegion.id !== values.secondaryRegion?.id

    // If region has changed. Fetch affected outputs and show confirmation dialog
    if (hasRegionChanged) {
      let affectedOutputs: AffectedOutput[] | undefined = undefined
      try {
        setIsFetchingOutputsAffectedByRegionChange(true)
        affectedOutputs = await fetchOutputsAffectedByRegionChange()
      } catch (err) {
        dispatch(enqueueErrorSnackbar({ error: err, operation: 'Failed to fetch outputs affected by region change' }))
        return
      } finally {
        setIsFetchingOutputsAffectedByRegionChange(false)
      }

      // Show confirmation dialog if any output is affected
      if (affectedOutputs.length > 0) {
        // Limit for number of outputs to show in confirm dialog
        const limit = 10
        const result = await showConfirmationAsync(
          <ChangeRegionDialog
            applianceName={appliance.name}
            newRegionName={region.name}
            affectedOutputs={affectedOutputs}
            limit={limit}
          />,
          undefined,
          { ok: { text: 'Accept' } },
        )
        if (result !== ConfirmationDialogResult.OK) {
          // Cancel submit
          return
        }
      }

      // Send all affected outputs in the update request to make sure conditions haven't changed
      // For example if another user creates another output on this appliance while the confirmation dialog is shown
      const acceptDisruptionsOutputIds = affectedOutputs.map((x) => x.id)
      values.acceptDisruption = {
        outputs: acceptDisruptionsOutputIds,
      }
    } else if (hasSecondaryRegionChanged) {
      // If secondary region has changed but not primary warn about reduced redundancy
      const secondaryRegionName = values.secondaryRegion?.name ?? null

      // Different dialogs depending on change or remove
      if (secondaryRegionName !== null) {
        const result = await showConfirmationAsync(
          <ChangeSecondaryRegionDialog applianceName={appliance.name} newSecondaryRegionName={secondaryRegionName} />,
          undefined,
          { ok: { text: 'Change secondary region' } },
        )
        if (result !== ConfirmationDialogResult.OK) {
          // Cancel submit
          return
        }
      } else {
        const result = await showConfirmationAsync(
          <RemoveSecondaryRegionDialog applianceName={appliance.name} />,
          undefined,
          { ok: { text: 'Remove secondary region', color: 'error' } },
        )
        if (result !== ConfirmationDialogResult.OK) {
          // Cancel submit
          return
        }
      }
    }

    // If owner of a port has changed. Show confirmation dialog
    const hasAnyPortOwnerChanged = values.ports.some((port) => port.owner !== port._owner.id)
    if (hasAnyPortOwnerChanged) {
      const result = await showConfirmationAsync(
        'You are changing owner of an interface. All inputs and outputs on this interface will be cleared. Are you sure you want to proceed?',
        undefined,
      )
      if (result !== ConfirmationDialogResult.OK) {
        // Cancel submit
        return
      }
    }

    if (appliance.type === ApplianceType.matroxMonarchEdgeE4_10Bit && appliance.settings.apiVersion === 'v2') {
      const applianceSettings = values.settings as MatroxE4ApplianceSettings | undefined
      const initialApplianceSettings = initialValues?.settings as MatroxE4ApplianceSettings | undefined
      const didUpdateIoConfig = applianceSettings?.ioConfig != initialApplianceSettings?.ioConfig
      if (didUpdateIoConfig) {
        const result = await showConfirmationAsync(
          'Updating the I/O config will reboot the Matrox system - all inputs and outputs on this appliance will be affected. Are you sure you want to proceed?',
          undefined,
        )
        if (result !== ConfirmationDialogResult.OK) {
          // Cancel submit
          return
        }
      }
    }

    // Send request to update appliance. Redirect to appliance list
    try {
      await dispatch(updateAppliance({ appliance, values: values, redirect: true }))
    } catch (err) {
      dispatch(enqueueErrorSnackbar({ error: err, operation: 'Failed to update appliance' }))
    }
  }

  const onDelete = (appliance: Appliance) => {
    showConfirmation(
      () => performDelete(appliance),
      <RemoveDialog appliance={appliance} hideConfirmDialog={hideConfirmation} />,
      undefined,
      {
        ok: { text: 'remove', color: 'error' },
      },
    )
  }

  const performRestartAppliance = (appliance: Appliance) => {
    showConfirmation(
      () => void dispatch(restartAppliance({ id: appliance.id, showSuccessSnackbar: true })),
      'Restarting will affect all video streams on the appliance. After restart the appliance will run the latest software version. Do you want to continue?',
      undefined,
      { ok: { text: 'restart' } },
    )
  }

  const performUpgradeImage = async (appliance: Appliance, imageVersion: string) => {
    const result = await showConfirmationAsync(
      'Upgrading the image will affect all video streams on the appliance. After the upgrade, the appliance will run the latest image version and software version. Do you want to continue?',
      appliance.name,
    )
    if (result !== ConfirmationDialogResult.OK) {
      return
    }

    await Api.appliancesApi.upgradeImage(appliance.id, imageVersion)
    commandStatuses.trigger()
  }

  const performRecreateTunnels = (appliance: Appliance) => {
    showConfirmation(
      () => void dispatch(recreateTunnels({ id: appliance.id })),
      'Recreating tunnels will affect all video streams on the appliance that involve RIST tunnels. Do you want to continue?',
    )
  }

  const performDelete = (appliance: Appliance) => {
    dispatch(removeAppliance(appliance.id))
    navigate(routes.appliances())
  }

  if (appliance && user.group && (appliance.owner as Group).id !== user.group && user.role !== Role.super) {
    return <Navigate to={routes.appliances()} />
  }

  const isCoreNode = appliance?.type === ApplianceType.core
  const isThumbNode = appliance?.type === ApplianceType.thumb
  const isApplianceOwner = user.group === getApplianceOwnerId(appliance)
  let showDeleteButton = false
  if (user.role === Role.super) {
    showDeleteButton = true
  } else if (!isCoreNode && !isThumbNode && user.role === Role.admin && isApplianceOwner) {
    showDeleteButton = true
  }

  const ports: ISharedPort[] = (appliance?._physicalPorts || []).map(
    ({ id: port, name, _owner, owner, addresses, networks }) => ({
      port,
      name,
      owner,
      networks: networks?.map((network) => network.id),
      _networks: networks,
      _address: addresses[0]?.address,
      _publicAddress: addresses[0]?.publicAddress,
      _interRegionPublicAddress: addresses[0]?.interRegionPublicAddress,
      _owner,
      _alarmsThatDisablePort: alarmsThatDisablePort(appliance, port),
    }),
  )

  const restartAction = (appliance: Appliance) => ({
    'Restart appliance': {
      onClick: () => performRestartAppliance(appliance),
      savingState: !!restarting,
    },
  })

  const upgradeImageAction = (appliance: Appliance) => {
    const getState = (): { disabled: false } | { disabled: true; tooltip: string } => {
      const canUpgrade = appliance?.settings.capabilities?.canUpgrade
      const controlImageVersion = appliance?.version.controlImageVersion
      const release = buildInfo?.release

      if (canUpgrade === undefined || controlImageVersion === undefined || release === undefined) {
        // If the image upgrade code is not available (for example if software upgrade needed)
        return { disabled: true, tooltip: 'Not supported. Make sure software is up-to-date' }
      }

      if (canUpgrade === false) {
        return { disabled: true, tooltip: 'Not supported for this appliance' }
      }

      if (release === controlImageVersion) {
        return { disabled: true, tooltip: 'Already up-to-date' }
      }

      return { disabled: false }
    }

    return {
      'Upgrade image': {
        onClick: () => {
          performUpgradeImage(appliance, releaseVersion)
        },
        ...getState(),
      },
    }
  }

  const recreateTunnelsAction = (appliance: Appliance) => {
    const recreate = {
      'Recreate tunnels': {
        onClick: () => performRecreateTunnels(appliance),
        savingState: !!recreatingTunnels,
      },
    } as const
    if (devMode) {
      return recreate
    } else {
      return {}
    }
  }

  const initialValues: undefined | ApplianceEditState = appliance
    ? {
        id: appliance.id,
        geoLocation: appliance.geoLocation || null,
        logLevel: appliance.logLevel,
        ristserverLogLevel: appliance.ristserverLogLevel,
        collectHostMetrics: appliance.collectHostMetrics || false,
        unhealthyAlarm: appliance.unhealthyAlarm,
        cordon: appliance.cordon,
        ports: ports,
        region: appliance.region || null,
        secondaryRegion: appliance.secondaryRegion || null,
        tags: appliance.tags,
        settings: {
          ...appliance.settings,
          useDynamicTunnelClientSourceAddress: appliance.settings.useDynamicTunnelClientSourceAddress ?? false,
          useEphemeralTunnelSenderPorts: appliance.settings.useEphemeralTunnelSenderPorts ?? false,
          proxy: Object.assign({ ...appliance.features.proxy }, appliance.settings.proxy),
        },
      }
    : undefined

  const savingState = saving === true || isFetchingOutputsAffectedByRegionChange

  return (
    <Wrapper name={['Appliances', appliance?.name]}>
      <Pendable pending={loading}>
        {initialValues && appliance && (
          <RHF<ApplianceEditState>
            onSubmit={async (values) => {
              const transformed = formTransform(values, {
                secondaryRegion: {
                  _transform: (v: string) => {
                    if (v === '' || v == null) {
                      return null
                    }
                    return v
                  },
                },
                collectHostMetrics: {
                  __transform: (v: string) => {
                    if (v === 'on') {
                      return true
                    }
                    return false
                  },
                },
                unhealthyAlarm: {
                  _transform: (v: 'off' | null) => v, // Don't transform null
                },
                cordon: {
                  _transform: (v: string) => !!v,
                },
              })
              if (values.geoLocation === null) {
                transformed['geoLocation'] = null
              }

              let hasNonDefaultProxySetting = false
              for (const key of Object.keys(transformed.settings.proxy) as Array<keyof ApplianceProxySettings>) {
                const defaultVal = appliance?.features?.proxy?.[key]
                const configuredVal = transformed.settings.proxy[key]
                const isDeepEqualToDefault = isEqual(configuredVal, defaultVal)
                const configIsEmptyArrayAndDefaultIsUndefined = isEqual(configuredVal, []) && defaultVal === undefined
                const propertyHasDefaultValue = isDeepEqualToDefault || configIsEmptyArrayAndDefaultIsUndefined
                if (!propertyHasDefaultValue) {
                  hasNonDefaultProxySetting = true
                  break
                } else {
                  transformed.settings.proxy[key] = null
                }
              }
              if (!hasNonDefaultProxySetting) {
                transformed.settings.proxy = null
              }

              await onSubmit(appliance, transformed)
            }}
            defaultValues={initialValues}
          >
            {(props) => (
              <>
                <SafeRouting formState={props.formState} />
                <Form id="appliance-form" noValidate>
                  <Meta appliance={appliance} />
                  <Paper title="Tags" collapsible>
                    <TagInputField formName={'tags'} type={'appliance'} commentProps={{ paddingBottom: '20px' }} />
                  </Paper>
                  <Settings
                    secondaryRegion={appliance.secondaryRegion}
                    region={appliance.region}
                    geoLocation={appliance.geoLocation}
                    type={appliance.type}
                    settings={props.getValues().settings as ApplianceSettings}
                  />
                  {(devMode && (
                    <AdvancedSettings
                      type={appliance.type}
                      settings={props.getValues().settings as ApplianceSettings}
                    />
                  )) ||
                    null}
                  <Interfaces ports={ports} appliance={appliance} />
                  {appliance.type !== ApplianceType.thumb && <InputsOutputs appliance={appliance} />}
                  {user.role === Role.super && <Tunnels appliance={appliance} />}
                  {devMode &&
                    user.role === Role.super &&
                    supportsPcap(appliance.version) &&
                    appliance.type !== ApplianceType.thumb && <Pcaps appliance={appliance} />}
                  {hasCommands && <CommandList statuses={commandStatuses.result} />}
                  <ButtonsPane
                    main={{
                      Cancel: {
                        onClick: () => navigate(routes.appliances()),
                      },
                      Save: { primary: true, savingState: savingState, type: 'submit' },
                    }}
                    secondary={
                      showDeleteButton
                        ? {
                            ...restartAction(appliance),
                            'Remove appliance': { onClick: () => onDelete(appliance) },
                            ...upgradeImageAction(appliance),
                            ...recreateTunnelsAction(appliance),
                          }
                        : restartAction(appliance)
                    }
                  />
                </Form>
              </>
            )}
          </RHF>
        )}
        {!appliance && error && !loading && <ApplicationError error={error} onOKButtonClicked={() => navigate(-1)} />}
      </Pendable>
    </Wrapper>
  )
}

const useCommandStatuses = (applianceId: Appliance['id']) => {
  const longCommandPollInterval = 10_000
  const shortCommandPollInterval = 1_000
  const [commandPollInterval, setCommandPollInterval] = useState(longCommandPollInterval)

  return useIntervalState(async () => {
    const commandInfos = await Api.appliancesApi.listApplianceCommands(applianceId)
    if (commandInfos.some((x) => x.status.type === 'in-progress' || x.status.type === 'not-started')) {
      setCommandPollInterval(shortCommandPollInterval)
    } else {
      setCommandPollInterval(longCommandPollInterval)
    }
    return commandInfos.map((c): CommandStatus => {
      const common = {
        id: c.id,
        title: mapCommandNameToTitle(c.name),
        createdAt: c.createdAt,
      }
      switch (c.status.type) {
        case 'not-started':
          return commandNotStarted({ ...common })
        case 'in-progress':
          return commandInProgress({
            ...common,
            progressPercentage: c.status.progress,
            message: c.status.status,
          })
        case 'error':
          return commandFailed({ ...common, message: c.status.error })
        case 'success':
          return commandSuccess({ ...common })
      }
    })
  }, commandPollInterval)
}

const mapCommandNameToTitle = (name: ApplianceCommandInfo['name']) => {
  switch (name) {
    case 'upgradeImage':
      return 'Upgrading container images'
    case 'upgradeEdgeOs':
      return 'Upgrading operating system'
  }
}
